<template>
  <v-window v-model="step" touchless>
    <v-window-item :value="1">
      <v-card-text v-if="step == 1" class="pa-0">
        <advice color="primary" marginLeft="0px">
          <span class="caption grey--text text--darken-1">{{
            $t('phone.advice')
          }}</span>
        </advice>

        <v-layout row wrap class="align-baseline mt-5">
          <v-flex
            xs12
            sm3
            lg3
            :class="{
              'text-xs-center': $vuetify.breakpoint.mdAndUp,
              'mb-3': $vuetify.breakpoint.smAndDown,
            }"
          >
            <flag-drop-down
              ref="flagDropdown"
              @change="onPrefixChanged"
              :value="form.step2.prefix"
              :disabled="disabled"
            ></flag-drop-down>
          </v-flex>
          <v-flex>
            <v-text-field
              v-model="form.step2.phone"
              :hint="$t('phone.hint')"
              :persistent-hint="true"
              :label="$t('profile.tab.phone')"
              :mask="maskRules"
              :rules="phoneRules"
              @change="onPhoneChanged"
              :disabled="disabled"
              required
            ></v-text-field>
          </v-flex>
          <v-flex xs12 offset-xs7>
            <div id="phone-validator"></div>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-window-item>

    <v-window-item :value="2">
      <v-card-text v-if="step == 2">
        <advice color="primary">
          <span
            v-html="
              $t('profile.editPhoneNumber.advice', {
                phoneNumberDisplay: phoneNumberDisplay,
              })
            "
          ></span>
        </advice>

        <v-layout row>
          <v-flex>
            <v-text-field
              v-model="verificationCode"
              mask="######"
              :label="$t('global.verification.code')"
              required
            ></v-text-field>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-window-item>
  </v-window>
</template>

<script>
import store from '@/store';
import FlagDropDown from '@/components/commons/FlagDropDown';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import registerService from '@/services/registerService';
import manageErrorService from '@/services/manageErrorService';
import Advice from '@/components/commons/Advice';

export default {
  name: 'EditPhoneNumber',
  components: {
    [FlagDropDown.name]: FlagDropDown,
    [Advice.name]: Advice,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    prefix: {
      type: Number,
      default: -1,
    },
    phone: {
      type: String,
      default: '',
    },
  },
  computed: {
    phoneNumberDisplay() {
      return `+${this.form.step2.prefix} ${this.form.step2.phone}`;
    },
    maskRules() {
      return '### ## ## ##';
    },
    phoneRules() {
      return [
        (v) => !!v || this.$i18n.t('global.validation.required.text'),
        (v) =>
          v.length === 9 ||
          this.$i18n.t('global.validation.equals', { number: 9 }),
      ];
    },
  },
  data() {
    return {
      step: 1,
      form: {
        step2: {
          prefix: store.state.countrySelected.prefix.replace('+', ''),
          phone: '',
        },
      },
      verificationCode: '',
      verificationId: '',
      isoCountrySelected: store.state.countrySelected.code.toLowerCase(),
      confirmationResult: null,
      recaptchaVerifier: null,
      errors: [],
      userCredentials: null,
    };
  },
  mounted() {
    this.reset();
    firebase.auth().languageCode = store.state.langSelected.lang;
  },
  methods: {
    onPrefixChanged(params) {
      this.form.step2.prefix = params.phonecode;
      this.isoCountrySelected = params.iso;
      this.$emit('on-phone-updated', {
        prefix: this.form.step2.prefix,
        phone: this.form.step2.phone,
      });
    },
    onPhoneChanged() {
      this.$emit('on-phone-updated', {
        prefix: this.form.step2.prefix,
        phone: this.form.step2.phone,
      });
    },
    async update(firebaseToken) {
      this.userCredentials = await firebase
        .auth()
        .signInWithCustomToken(firebaseToken);
      await this.onVerifyPhoneNumber();
    },
    async onVerifyPhoneNumber() {
      let self = this;

      if (this.recaptchaVerifier === null) {
        this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
          'phone-validator',
          {
            size: 'normal',
            callback: function (response) {
              if (self.step === 2) {
                self.onVerifyPhoneNumber();
              }
            },
          }
        );
      }

      try {
        let phoneNumber = `+${this.form.step2.prefix}${this.form.step2.phone}`;
        let provider = new firebase.auth.PhoneAuthProvider();
        let verificationId = await provider.verifyPhoneNumber(
          phoneNumber,
          this.recaptchaVerifier
        );
        this.verificationId = verificationId;
      } catch (e) {
        let dataError = {};
        if (e.code === 'auth/too-many-requests') {
          dataError.error = this.$i18n.t(e.code);
        }
        manageErrorService.handle(dataError);
        this.recaptchaVerifier.clear();
        this.recaptchaVerifier = null;
        this.goToStep1();
        return;
      }

      this.step = 2;
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
    },
    async onVerifyPhoneCode() {
      let phoneCredential = await firebase.auth.PhoneAuthProvider.credential(
        this.verificationId,
        this.verificationCode
      );
      try {
        await this.userCredentials.user.updatePhoneNumber(phoneCredential);
        let results = await registerService.validatePhone();
        if (results.error) {
          manageErrorService.handle(results.data);
          return false;
        }
      } catch (error) {
        if (error.code === 'auth/invalid-verification-code') {
          return false;
        } else if (error.code === 'auth/credential-already-in-use') {
          await registerService.validatePhone();
        }
      }
      return true;
    },
    goToStep1() {
      this.step = 1;

      this.setOnBackDefaultValues();
    },
    reset() {
      this.step = 1;
      this.form.step2.prefix = store.state.countrySelected.prefix.replace(
        '+',
        ''
      );
      this.form.step2.phone = '';
      this.verificationCode = '';
      this.verificationId = '';
      this.isoCountrySelected = store.state.countrySelected.code.toLowerCase();

      this.setOnBackDefaultValues();
    },
    setOnBackDefaultValues() {
      this.$nextTick(() => {
        if (this.prefix !== -1) {
          this.form.step2.prefix = this.prefix;
        }

        if (this.phone !== '') {
          this.form.step2.phone = this.phone;
        }

        if (this.$refs.flagDropdown !== 'undefined') {
          this.$refs.flagDropdown.reset();
        }
      });

      if (this.recaptchaVerifier !== null) {
        this.recaptchaVerifier.clear();
        this.recaptchaVerifier = null;
      }
    },
    setValues(params) {
      this.form.step2.prefix = params.prefix;
      this.form.step2.phone = params.phone;
      this.$emit('on-phone-updated', {
        prefix: this.form.step2.prefix,
        phone: this.form.step2.phone,
      });
    },
  },
};
</script>
